import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { Tile, PlayerId, GameState, GridHint, WordGrid, SolvedWord } from "../../shared/types";
import { api } from "../lib/network";
import { useGameStore } from "../stores/useGameStore";
import { usePolling } from "../hooks/usePolling";
import { sfx } from "../lib/audio";
import GameBoard, { type GameBoardHandle } from "../components/game/GameBoard";
import TileRack from "../components/game/TileRack";
import Timer from "../components/game/Timer";
import ResultsOverlay from "../components/game/ResultsOverlay";
import { GridCluePanel, ArcadeWordPanel } from "../components/game/CluePanels";
import { probeClueForCell, type ClueProbeState } from "../lib/clueProbe";

// Old games persisted a single SolvedWord per word; newer games store an
// array (one entry per player). Normalize so both shapes render.
function normalizeSolvedWords(raw: Record<string, SolvedWord | SolvedWord[]>): Record<string, SolvedWord[]> {
  const out: Record<string, SolvedWord[]> = {};
  for (const [k, v] of Object.entries(raw || {})) {
    out[k] = Array.isArray(v) ? v : [v];
  }
  return out;
}

export default function Game() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [playerRole, setPlayerRole] = useState<PlayerId | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTile, setSelectedTile] = useState<Tile | null>(null);
  const [room, setRoom] = useState<{
    p1Name: string;
    p2Name?: string;
    aiDifficulty: string | null;
    mode: "arcade" | "grid";
    complexity: "low" | "medium" | "high";
    timeLimitMs: number;
    handicap: "none" | "light" | "heavy";
  } | null>(null);
  const [myName, setMyName] = useState("You");
  const [penaltyFlash, setPenaltyFlash] = useState(false);
  const [highlightedClueId, setHighlightedClueId] = useState<string | null>(null);
  const [replayBusy, setReplayBusy] = useState(false);

  const game = useGameStore();

  const gameStartedRef = useRef(false);
  const clueProbeRef = useRef<ClueProbeState | null>(null);
  const desktopBoardRef = useRef<GameBoardHandle>(null);
  const mobileBoardRef = useRef<GameBoardHandle>(null);
  const isDesktopRef = useRef(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    isDesktopRef.current = isDesktop;
  }, [isDesktop]);
  // The page renders both a desktop and a mobile GameBoard; imperative calls
  // (confetti, score pops, clue highlights) must target the visible one.
  const activeBoard = useCallback(
    () => (isDesktopRef.current ? desktopBoardRef.current : mobileBoardRef.current),
    []
  );
  const [mobileCluesOpen, setMobileCluesOpen] = useState(false);
  const prevScoreRef = useRef(0);
  const prevPhaseRef = useRef<string>("");
  const prevBoardLenRef = useRef(0);
  const timeAnchorRef = useRef<{ remainingMs: number; at: number } | null>(null);
  const timeFallbackRef = useRef({ startedAt: 0, timeLimitMs: 0 });
  const touchDragRef = useRef<{
    tile: Tile;
    sideIndex: number;
    index: number;
    ghost: HTMLElement | null;
  } | null>(null);

  // Determine player role from room metadata
  useEffect(() => {
    async function init() {
      if (!roomId) return;
      try {
        const saved = localStorage.getItem("sw_player");
        if (!saved) {
          navigate("/");
          return;
        }
        const player = JSON.parse(saved);
        if (player.username) setMyName(player.username);

        const roomRes = await fetch(`/api/rooms/${roomId}`);
        if (!roomRes.ok) {
          setError("Room not found");
          setLoading(false);
          return;
        }
        const roomData = await roomRes.json() as {
          player1Id: string; player2Id: string | null;
          p1Name: string; p2Name?: string; aiDifficulty: string | null;
          mode: "arcade" | "grid";
          complexity: "low" | "medium" | "high";
          timeLimitMs: number;
          handicap: "none" | "light" | "heavy";
        };
        setRoom({
          p1Name: roomData.p1Name,
          p2Name: roomData.p2Name,
          aiDifficulty: roomData.aiDifficulty,
          mode: roomData.mode,
          complexity: roomData.complexity,
          timeLimitMs: roomData.timeLimitMs,
          handicap: roomData.handicap,
        });
        if (roomData.player1Id === player.id) {
          setPlayerRole("p1");
        } else if (roomData.player2Id === player.id) {
          setPlayerRole("p2");
        } else {
          setError("You are not in this room");
          setLoading(false);
          return;
        }

        // Try to load existing game
        try {
          const state = await api.getGame(roomId, playerRole || undefined);
          syncGameState(state, playerRole || "p1");
          gameStartedRef.current = true;
        } catch {
          // No game started yet
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load game");
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [roomId, navigate, playerRole]);

  // Sync server state to store
  const syncGameState = useCallback((state: GameState, myRole: PlayerId) => {
    const opponentRole = myRole === "p1" ? "p2" : "p1";
    game.setGame({
      roomId: state.roomId,
      mode: state.mode,
      phase: state.phase,
      myBoard: state.playerBoards[myRole] || [],
      opponentBoard: state.playerBoards[opponentRole] || [],
      premiumSquares: state.premiumSquares,
      myRack: state.playerRacks[myRole] || [],
      opponentRack: state.playerRacks[opponentRole] || [],
      myScore: state.scores[myRole] || 0,
      opponentScore: state.scores[opponentRole] || 0,
      myPenalties: state.penalties[myRole] || 0,
      opponentPenalties: state.penalties[opponentRole] || 0,
      timeLimitMs: state.timeLimitMs,
      complexity: state.complexity,
      winner: state.winner,
      currentTurn: state.currentTurn,
      eventLog: state.eventLog,
      startedAt: state.startedAt,
      puzzle: state.puzzle,
      solvedWords: normalizeSolvedWords(state.solvedWords),
      firstCompletedBy: state.firstCompletedBy,
      firstCompletedAt: state.firstCompletedAt,
      gracePeriodEnd: state.gracePeriodEnd,
    });

    // Re-anchor the countdown to the server-reported remaining time so a
    // skewed player device clock can't desync or fake the timer.
    timeFallbackRef.current = { startedAt: state.startedAt, timeLimitMs: state.timeLimitMs };
    const serverRemaining = state.timers?.[myRole];
    if (typeof serverRemaining === "number") {
      timeAnchorRef.current = { remainingMs: Math.max(0, serverRemaining), at: Date.now() };
    } else if (state.startedAt > 0) {
      timeAnchorRef.current = {
        remainingMs: Math.max(0, state.timeLimitMs - (Date.now() - state.startedAt)),
        at: Date.now(),
      };
    }
  }, [game]);

  // Server-anchored remaining time: use the last server value and tick
  // locally between polls (local clock deltas are immune to fixed skew).
  const getRemainingMs = useCallback(() => {
    const anchor = timeAnchorRef.current;
    if (anchor) return Math.max(0, anchor.remainingMs - (Date.now() - anchor.at));
    const fb = timeFallbackRef.current;
    if (fb.startedAt > 0) return Math.max(0, fb.timeLimitMs - (Date.now() - fb.startedAt));
    return 0;
  }, []);

  // Poll for game state updates
  usePolling(
    useCallback(async () => {
      if (!roomId || !playerRole) return;
      try {
        const state = await api.getGame(roomId, playerRole);
        syncGameState(state, playerRole);
        gameStartedRef.current = true;
      } catch {
        // Game may not exist yet; keep polling
      }
    }, [roomId, playerRole, syncGameState]),
    playerRole !== null && game.phase !== "game_over",
    1500
  );

  // Auto-end when timer runs out
  useEffect(() => {
    if (game.phase === "playing" && timeAnchorRef.current && getRemainingMs() <= 0) {
      // Time's up - game will end on server next poll
      sfx.invalid();
    }
  }, [game.phase, getRemainingMs]);

  // Penalty flash effect
  useEffect(() => {
    if (penaltyFlash) {
      const timer = setTimeout(() => setPenaltyFlash(false), 500);
      return () => clearTimeout(timer);
    }
  }, [penaltyFlash]);

  // Animation: victory confetti on game over
  useEffect(() => {
    if (game.phase === "game_over" && prevPhaseRef.current !== "game_over") {
      const isWinner = playerRole === game.winner;
      if (isWinner) {
        activeBoard()?.spawnVictoryConfetti();
        sfx.victory();
      } else if (game.winner === null) {
        setTimeout(() => activeBoard()?.spawnVictoryConfetti(), 500);
      }
    }
    prevPhaseRef.current = game.phase;
  }, [game.phase, game.winner, playerRole]);

  // Animation: score float on score increase
  useEffect(() => {
    const currentScore = game.myScore;
    if (currentScore > prevScoreRef.current && prevScoreRef.current > 0) {
      const gain = currentScore - prevScoreRef.current;
      const center = Math.floor(game.myBoard.length > 0 ? game.myBoard[game.myBoard.length - 1].x : 7);
      const cy = Math.floor(game.myBoard.length > 0 ? game.myBoard[game.myBoard.length - 1].y : 7);
      activeBoard()?.floatScore(center, cy, gain);
    }
    prevScoreRef.current = currentScore;
  }, [game.myScore, game.myBoard]);

  // Track board length for placement detection
  useEffect(() => {
    prevBoardLenRef.current = game.myBoard.length;
  }, [game.myBoard.length]);

  const handleSelectTile = useCallback((tile: Tile) => {
    if (game.phase !== "playing") return;
    setSelectedTile((prev) => (prev?.id === tile.id ? null : tile));
    sfx.tilePickup();
  }, [game.phase]);

  const handleClueClick = useCallback((clue: GridHint) => {
    activeBoard()?.highlightClue(clue);
  }, []);

  // Clicking the first square of a word probes its clue in the hints panel
  // (across first, down on a repeat click of the same square).
  const probeClue = useCallback((x: number, y: number): boolean => {
    if (game.mode !== "grid" || !game.puzzle || game.phase !== "playing") return false;
    const result = probeClueForCell(game.puzzle.clues, x, y, clueProbeRef.current);
    if (!result) return false;
    clueProbeRef.current = result.state;
    setHighlightedClueId(result.clueId);
    return true;
  }, [game.mode, game.puzzle, game.phase]);

  // Server errors are the raw JSON body ({ error, penalty, state }). Show only
  // the human-readable error string and re-sync state from the response.
  const handlePlacementError = useCallback((e: unknown) => {
    sfx.invalid();
    setPenaltyFlash(true);
    const raw = e instanceof Error ? e.message : "Invalid placement";
    let msg = raw;
    try {
      const errorData = JSON.parse(raw);
      if (typeof errorData.error === "string") msg = errorData.error;
      if (errorData.state && playerRole) {
        syncGameState(errorData.state, playerRole);
      }
    } catch {
      // Not a JSON error; show the raw message
    }
    setError(msg);
    setTimeout(() => setError(""), 2000);
  }, [playerRole, syncGameState]);

  const handleCellClick = useCallback(async (x: number, y: number) => {
    if (!playerRole || !roomId || game.phase !== "playing") return;

    if (!selectedTile) {
      // No tile in hand: clicking a word's first square probes its clue.
      probeClue(x, y);
      return;
    }

    // Check if cell already occupied on my board
    if (game.myBoard.some((t) => t.x === x && t.y === y)) return;

    // In grid mode, only allow placement on valid puzzle squares
    if (game.mode === "grid" && game.puzzle) {
      const key = `${x},${y}`;
      if (!game.puzzle.solution[key] || game.puzzle.blackSquares.includes(key)) {
        sfx.invalid();
        setError("Cannot place tile on a black square");
        setTimeout(() => setError(""), 2000);
        return;
      }
    }

    try {
      const state = await api.placeTile(roomId, {
        player: playerRole,
        tileId: selectedTile.id,
        letter: selectedTile.letter,
        x,
        y,
      });
      syncGameState(state, playerRole);
      setSelectedTile(null);
      setHighlightedClueId(null);
      sfx.tilePlace();
      activeBoard()?.clearClueHighlight();
    } catch (e) {
      handlePlacementError(e);
    }
  }, [selectedTile, playerRole, roomId, game.phase, game.myBoard, game.mode, game.puzzle, syncGameState, handlePlacementError, probeClue]);

  const handleDropTile = useCallback(async (tile: Tile, x: number, y: number) => {
    if (!playerRole || !roomId || game.phase !== "playing") return;

    // Check if cell already occupied on my board
    if (game.myBoard.some((t) => t.x === x && t.y === y)) return;

    // In grid mode, only allow placement on valid puzzle squares
    if (game.mode === "grid" && game.puzzle) {
      const key = `${x},${y}`;
      if (!game.puzzle.solution[key] || game.puzzle.blackSquares.includes(key)) {
        sfx.invalid();
        setError("Cannot place tile on a black square");
        setTimeout(() => setError(""), 2000);
        return;
      }
    }

    try {
      const state = await api.placeTile(roomId, {
        player: playerRole,
        tileId: tile.id,
        letter: tile.letter,
        x,
        y,
      });
      syncGameState(state, playerRole);
      setSelectedTile(null);
      setHighlightedClueId(null);
      sfx.tilePlace();
      activeBoard()?.clearClueHighlight();
    } catch (e) {
      handlePlacementError(e);
    }
  }, [playerRole, roomId, game.phase, game.myBoard, game.mode, game.puzzle, syncGameState, handlePlacementError]);

  const handleTileClick = useCallback(
    async (tileId: string, _letter: string, x: number, y: number) => {
      if (!playerRole || !roomId || game.phase !== "playing") return;

      // First square of a word: probe the clue instead of recalling the tile.
      if (probeClue(x, y)) return;

      try {
        const state = await api.recallTile(roomId, {
          player: playerRole,
          tileId,
        });
        syncGameState(state, playerRole);
        setHighlightedClueId(null);
        sfx.tilePickup();
      } catch (e) {
        sfx.invalid();
      }
    },
    [playerRole, roomId, game.phase, syncGameState, probeClue]
  );

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-vsw-300">Loading game...</div>
      </div>
    );
  }

  if (error && !gameStartedRef.current) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error}</div>
        <button
          onClick={() => navigate("/")}
          className="bg-vsw-500 text-white px-4 py-2 rounded"
        >
          Go Home
        </button>
      </div>
    );
  }

  // Build solved words keys for opponent hint borders
  const opponentSolvedKeys = new Set<string>();
  Object.values(game.solvedWords).forEach((entries) => {
    entries.forEach((sw) => {
      if (sw.playerId !== playerRole) {
        sw.keys.forEach((k) => opponentSolvedKeys.add(k));
      }
    });
  });

  return (
    <div className="w-full h-full flex flex-col">
      {/* Penalty flash overlay */}
      {penaltyFlash && (
        <div className="fixed inset-0 bg-red-500/20 pointer-events-none z-50 animate-pulse" />
      )}

      {/* HUD */}
      <div className="flex items-center justify-between px-2 sm:px-4 py-2 bg-board-cell border-b border-board-border">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="text-vsw-200 font-medium text-sm sm:text-base">{game.mode === "arcade" ? "Arcade" : "Grid"}</div>
          <div className="text-vsw-300 text-xs sm:text-sm capitalize">{game.complexity}</div>
        </div>
        <div className="flex items-center gap-2 sm:gap-6">
          <div className="text-center">
            <div className="text-[10px] sm:text-xs text-vsw-300 truncate max-w-[60px]">{myName}</div>
            <div className="text-lg sm:text-xl font-bold text-vsw-400">{game.myScore}</div>
            {game.myPenalties > 0 && (
              <div className="text-[10px] text-red-400">-{game.myPenalties}</div>
            )}
          </div>
          <Timer getRemainingMs={getRemainingMs} />
          <div className="text-center">
            <div className="text-[10px] sm:text-xs text-vsw-300">
              {room?.aiDifficulty
                ? `AI (${room.aiDifficulty.charAt(0).toUpperCase() + room.aiDifficulty.slice(1)})`
                : room?.p2Name || "Opponent"}
            </div>
            <div className="text-lg sm:text-xl font-bold text-vsw-400">{game.opponentScore}</div>
            {game.opponentPenalties > 0 && (
              <div className="text-[10px] text-red-400">-{game.opponentPenalties}</div>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate("/")}
          className="text-xs sm:text-sm text-vsw-300 hover:text-vsw-200"
        >
          Leave
        </button>
      </div>

      {/* Game area with 4-sided rack */}
      <div className="flex-1 overflow-hidden p-2 sm:p-4">
        {/* Desktop: centered layout — side racks hug the board, hints in the upper right when space allows */}
        <div className="hidden lg:flex h-full items-center justify-center gap-3 relative">
          {/* Left rack */}
          <div className="min-h-0">
            <TileRack
              tiles={game.rackSides[3]}
              sideIndex={3}
              selectedTileId={selectedTile?.id}
              onSelectTile={handleSelectTile}
              onDragStartTile={() => {}}
              onMoveTile={(fromIndex, toIndex) => game.moveTileWithinSide(3, fromIndex, toIndex)}
              onMoveTileBetweenSides={(fromSide, fromIndex, toSide, toIndex) =>
                game.moveTileBetweenSides(fromSide, fromIndex, toSide, toIndex)
              }
              disabled={game.phase !== "playing"}
              touchDragRef={touchDragRef}
              orientation="left"
            />
          </div>

          {/* Center: top rack / board / bottom rack / fallback clues */}
          <div className="h-full flex flex-col gap-3 min-h-0" style={{ width: "min(760px, 100%)" }}>
            <TileRack
              tiles={game.rackSides[0]}
              sideIndex={0}
              selectedTileId={selectedTile?.id}
              onSelectTile={handleSelectTile}
              onDragStartTile={() => {}}
              onMoveTile={(fromIndex, toIndex) => game.moveTileWithinSide(0, fromIndex, toIndex)}
              onMoveTileBetweenSides={(fromSide, fromIndex, toSide, toIndex) =>
                game.moveTileBetweenSides(fromSide, fromIndex, toSide, toIndex)
              }
              disabled={game.phase !== "playing"}
              touchDragRef={touchDragRef}
              orientation="top"
            />

            {error && (
              <div className="bg-red-500/20 text-red-300 px-3 py-1 rounded text-xs sm:text-sm">
                {error}
              </div>
            )}

            <div className="flex-1 min-h-0">
              <GameBoard
                ref={desktopBoardRef}
                premiumSquares={game.premiumSquares}
                board={game.myBoard}
                puzzle={game.puzzle}
                opponentSolvedKeys={opponentSolvedKeys}
                onCellClick={handleCellClick}
                onTileClick={handleTileClick}
                onDropTile={handleDropTile}
                animateNewTiles={true}
                touchDragRef={touchDragRef}
              />
            </div>

            <TileRack
              tiles={game.rackSides[2]}
              sideIndex={2}
              selectedTileId={selectedTile?.id}
              onSelectTile={handleSelectTile}
              onDragStartTile={() => {}}
              onMoveTile={(fromIndex, toIndex) => game.moveTileWithinSide(2, fromIndex, toIndex)}
              onMoveTileBetweenSides={(fromSide, fromIndex, toSide, toIndex) =>
                game.moveTileBetweenSides(fromSide, fromIndex, toSide, toIndex)
              }
              disabled={game.phase !== "playing"}
              touchDragRef={touchDragRef}
              orientation="bottom"
            />

            {/* Clues below the board until the upper-right corner panel fits (≥1400px) */}
            <div className="min-[1400px]:hidden h-28 overflow-y-auto min-h-0">
              <div className="w-80 max-w-full mx-auto h-full">
                {game.mode === "grid" && game.puzzle && (
                  <GridCluePanel clues={game.puzzle.clues} solvedWords={game.solvedWords} onClueClick={handleClueClick} highlightedClueId={highlightedClueId} />
                )}
                {game.mode === "arcade" && game.puzzle && (
                  <ArcadeWordPanel puzzle={game.puzzle} solvedWords={game.solvedWords} />
                )}
              </div>
            </div>
          </div>

          {/* Right rack */}
          <div className="min-h-0">
            <TileRack
              tiles={game.rackSides[1]}
              sideIndex={1}
              selectedTileId={selectedTile?.id}
              onSelectTile={handleSelectTile}
              onDragStartTile={() => {}}
              onMoveTile={(fromIndex, toIndex) => game.moveTileWithinSide(1, fromIndex, toIndex)}
              onMoveTileBetweenSides={(fromSide, fromIndex, toSide, toIndex) =>
                game.moveTileBetweenSides(fromSide, fromIndex, toSide, toIndex)
              }
              disabled={game.phase !== "playing"}
              touchDragRef={touchDragRef}
              orientation="right"
            />
          </div>

          {/* Hints in the upper-right corner when there is space (kept out of flow so the board stays centered) */}
          <div className="hidden min-[1400px]:block absolute top-2 right-2 z-10 w-56 max-h-full overflow-y-auto">
            {game.mode === "grid" && game.puzzle && (
              <GridCluePanel clues={game.puzzle.clues} solvedWords={game.solvedWords} onClueClick={handleClueClick} highlightedClueId={highlightedClueId} />
            )}
            {game.mode === "arcade" && game.puzzle && (
              <ArcadeWordPanel puzzle={game.puzzle} solvedWords={game.solvedWords} />
            )}
          </div>
        </div>

        {/* Mobile: stacked layout */}
        <div className="lg:hidden flex flex-col gap-2 h-full overflow-y-auto pb-2">
          {/* Top rack */}
          <div className="w-full flex justify-center">
            <TileRack
              tiles={game.rackSides[0]}
              sideIndex={0}
              selectedTileId={selectedTile?.id}
              onSelectTile={handleSelectTile}
            onDragStartTile={() => {}}
            onMoveTile={(fromIndex, toIndex) => game.moveTileWithinSide(0, fromIndex, toIndex)}
            onMoveTileBetweenSides={(fromSide, fromIndex, toSide, toIndex) =>
              game.moveTileBetweenSides(fromSide, fromIndex, toSide, toIndex)
            }
            disabled={game.phase !== "playing"}
            touchDragRef={touchDragRef}
            orientation="top"
          />
          </div>

          {error && (
            <div className="bg-red-500/20 text-red-300 px-3 py-1 rounded text-xs sm:text-sm">
              {error}
            </div>
          )}

          <div className="h-[52vh] min-h-[300px] shrink-0 flex items-center justify-center">
            <GameBoard
              ref={mobileBoardRef}
              premiumSquares={game.premiumSquares}
              board={game.myBoard}
              puzzle={game.puzzle}
opponentSolvedKeys={opponentSolvedKeys}
              onCellClick={handleCellClick}
              onTileClick={handleTileClick}
              onDropTile={handleDropTile}
              animateNewTiles={true}
              touchDragRef={touchDragRef}
            />
          </div>

          {/* Remaining racks (wrap on narrow screens) */}
          {([2, 3, 1] as const).map((side) => (
            <div key={side} className="w-full flex justify-center">
              <TileRack
                tiles={game.rackSides[side]}
                sideIndex={side}
                selectedTileId={selectedTile?.id}
                onSelectTile={handleSelectTile}
                onDragStartTile={() => {}}
                onMoveTile={(fromIndex, toIndex) => game.moveTileWithinSide(side, fromIndex, toIndex)}
                onMoveTileBetweenSides={(fromSide, fromIndex, toSide, toIndex) =>
                  game.moveTileBetweenSides(fromSide, fromIndex, toSide, toIndex)
                }
                disabled={game.phase !== "playing"}
                touchDragRef={touchDragRef}
                orientation="top"
              />
            </div>
          ))}

          {/* Mobile clues */}
          <div className="w-full flex justify-center max-h-56 overflow-y-auto">
            {game.mode === "grid" && game.puzzle && (
              <GridCluePanel clues={game.puzzle.clues} solvedWords={game.solvedWords} onClueClick={handleClueClick} highlightedClueId={highlightedClueId} />
            )}
            {game.mode === "arcade" && game.puzzle && (
              <ArcadeWordPanel puzzle={game.puzzle} solvedWords={game.solvedWords} />
            )}
          </div>
        </div>
      </div>

      {/* Mobile: floating clues quick-access (button + bottom sheet) */}
      {game.puzzle && game.phase === "playing" && (
        <>
          <button
            onClick={() => setMobileCluesOpen((v) => !v)}
            className="lg:hidden fixed bottom-4 right-4 z-40 bg-vsw-500 text-white font-bold px-4 py-2.5 rounded-full shadow-lg text-sm active:scale-95"
          >
            {mobileCluesOpen ? "Hide Clues" : "View Clues"}
          </button>
          {mobileCluesOpen && (
            <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-board-cell border-t border-line rounded-t-2xl shadow-2xl p-3 pb-16 max-h-[55vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-white">Clues</h3>
                <button
                  onClick={() => setMobileCluesOpen(false)}
                  aria-label="Close clues"
                  className="text-white/60 text-2xl leading-none px-2 -my-2"
                >
                  ×
                </button>
              </div>
              {game.mode === "grid" && (
                <GridCluePanel
                  clues={game.puzzle.clues}
                  solvedWords={game.solvedWords}
                  highlightedClueId={highlightedClueId}
                  onClueClick={(c) => {
                    handleClueClick(c);
                    setMobileCluesOpen(false);
                  }}
                />
              )}
              {game.mode === "arcade" && <ArcadeWordPanel puzzle={game.puzzle} solvedWords={game.solvedWords} />}
            </div>
          )}
        </>
      )}

      {/* Results */}
      {game.phase === "game_over" && (
        <ResultsOverlay
          scores={[game.myScore, game.opponentScore]}
          winner={game.winner}
          p1Name="Player 1"
          p2Name="Player 2"
          onPlayAgain={async () => {
            game.reset();
            if (!room) {
              navigate("/");
              return;
            }
            try {
              const saved = localStorage.getItem("sw_player");
              if (!saved) {
                navigate("/");
                return;
              }
              const player = JSON.parse(saved);
              const newRoom = await api.createRoom({
                playerId: player.id,
                mode: room.mode,
                complexity: room.complexity,
                timeLimitMs: room.timeLimitMs,
                aiDifficulty: room.aiDifficulty as any,
                handicap: room.handicap,
              });
              if (room.aiDifficulty) {
                await api.joinRoom(newRoom.roomId, player.id);
                await api.startGame({
                  roomId: newRoom.roomId,
                  mode: room.mode,
                  complexity: room.complexity,
                  timeLimitMs: room.timeLimitMs,
                  aiDifficulty: room.aiDifficulty,
                  handicap: room.handicap,
                });
              }
              navigate(`/game/${newRoom.roomId}`);
            } catch {
              navigate("/");
            }
          }}
          onHome={() => {
            game.reset();
            navigate("/");
          }}
          onViewReplay={async () => {
            if (!roomId || replayBusy) return;
            setReplayBusy(true);
            try {
              // The worker persists the replay on the first request that sees
              // game_over; if the game expired client-side first, nudge the
              // server with a state fetch (checkTimeExpiry + saveReplayIfOver)
              // and poll until the replay exists so the replay page never 404s.
              for (let i = 0; i < 12; i++) {
                try {
                  await api.getReplay(roomId);
                  break;
                } catch {
                  if (i === 0) {
                    try { await api.getGame(roomId); } catch { /* game state expired */ }
                  }
                  await new Promise((r) => setTimeout(r, 700));
                }
              }
            } finally {
              setReplayBusy(false);
            }
            navigate(`/replay/${roomId}`);
          }}
          replayBusy={replayBusy}
        />
      )}
    </div>
  );
}
